import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { apiRoutes } from '../api';
import { prefetchedLocationAccess } from '../api/prefetch';
import { getConfig } from '../config/environment';

const GeoLocationContext = createContext(undefined);

// Friendly names for the geo codes the backend stores for inclusion restrictions.
const PROVINCE_NAMES = {
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador', NS: 'Nova Scotia', NT: 'Northwest Territories',
  NU: 'Nunavut', ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec',
  SK: 'Saskatchewan', YT: 'Yukon',
};
const COUNTRY_NAMES = {
  CA: 'Canada', CAN: 'Canada', US: 'United States', USA: 'United States',
};

function friendlyName(value, map) {
  if (!value) return '';
  return map[String(value).toUpperCase()] || value;
}

// Fallback used only when the backend hasn't returned the org's allowed regions
// (e.g. older API build or cached pre-upgrade result).
const FALLBACK_ALLOWED_REGIONS = [{ label: 'Canada' }];

/**
 * Normalize the backend's allowed-region records into display labels.
 * Each record looks like: { type: 'country'|'state'|'city', code, country, state, city }
 */
function normalizeAllowedRegions(apiRegions) {
  if (!Array.isArray(apiRegions)) return null; // null = backend didn't provide the field
  return apiRegions
    .map((r) => {
      if (!r) return null;
      if (r.type === 'city' && r.city) {
        const prov = friendlyName(r.state || r.code, PROVINCE_NAMES);
        const label = [r.city, prov].filter(Boolean).join(', ');
        return label ? { label } : null;
      }
      if (r.type === 'state') {
        const provCode = String(r.state || r.code || '').toUpperCase();
        const prov = friendlyName(provCode, PROVINCE_NAMES);
        const country = PROVINCE_NAMES[provCode] ? 'Canada' : '';
        const label = [prov, country].filter(Boolean).join(', ');
        return label ? { label } : null;
      }
      // country-level inclusion
      const label = friendlyName(r.country || r.code, COUNTRY_NAMES);
      return label ? { label } : null;
    })
    .filter(Boolean);
}

const CACHE_KEY = 'geo_location_cache';
const CACHE_DURATION = 1000 * 60 * 30;

function getCachedLocation() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    const parsed = JSON.parse(cached);
    if (Date.now() - parsed.timestamp > CACHE_DURATION) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

function setCachedLocation(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {
    // silent fail
  }
}

/**
 * Check geolocation permission state without triggering the prompt.
 */
/**
 * Returns true when running inside Facebook, Instagram, Messenger, TikTok, or
 * similar in-app browsers that block navigator.geolocation. These environments
 * expose the UA strings below but silently time-out any getCurrentPosition call,
 * so we skip browser geo entirely and fall straight through to the IP path.
 */
function isInAppBrowser() {
  const ua = navigator.userAgent || '';
  return /FBAN|FBAV|Instagram|FB_IAB|FBIOS|FBANDROID|Messenger|MessengerForiOS|TikTok|musical_ly/i.test(ua);
}

async function getGeoPermissionState() {
  // In-app browsers block geolocation — treat as denied so we skip the timeout.
  if (isInAppBrowser()) return 'denied';
  try {
    if (!navigator.permissions) return 'unknown';
    const result = await navigator.permissions.query({ name: 'geolocation' });
    return result.state; // 'granted', 'prompt', or 'denied'
  } catch {
    return 'unknown';
  }
}

function getBrowserPosition(timeout = 5000) {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) {
      resolve({ latitude: 0, longitude: 0, status: 'unavailable' });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, status: 'granted' }),
      (err) => {
        const status = err?.code === 1 ? 'denied' : err?.code === 3 ? 'timed_out' : 'error';
        resolve({ latitude: 0, longitude: 0, status, errorCode: err?.code });
      },
      { timeout, maximumAge: 300000 }
    );
  });
}

export function GeoLocationProvider({ children }) {
  const config = getConfig();
  const geoBlockStage = config.geoBlockStage || 'SiteLoad';
  
  // Check if been manually bypassed
  const isBypassed = localStorage.getItem('geo_bypass_acknowledged') === 'true';

  // Initialize from cache to avoid blocking the first render (LCP optimization).
  // If we have a valid cache OR location isn't required OR bypassed, isChecking starts false.
  const cachedInit = (!config.requireLocation || isBypassed) ? { isAllowed: true } : getCachedLocation();
  const isInitialSiteLoad = geoBlockStage === 'SiteLoad';

  const [locationData, setLocationData] = useState(cachedInit || null);
  const [isAllowed, setIsAllowed] = useState(cachedInit ? cachedInit.isAllowed : true);
  const [isChecking, setIsChecking] = useState(isInitialSiteLoad && !cachedInit);
  const [hasVerified, setHasVerified] = useState(!!cachedInit);
  const [permissionState, setPermissionState] = useState(cachedInit?.permissionState || null);
  const [isLocationDeniedOrIgnored, setIsLocationDeniedOrIgnored] = useState(
    cachedInit?.isLocationDeniedOrIgnored || false
  );
  const [error, setError] = useState(null);

  const checkLocation = useCallback(async () => {
    const config = getConfig();

    // Skip geo check entirely if the site doesn't require location verification
    if (!config.requireLocation) {
      setIsAllowed(true);
      setIsChecking(false);
      setHasVerified(true);
      return;
    }

    // Short-circuit check if bypass key exists
    if (localStorage.getItem('geo_bypass_acknowledged') === 'true') {
      setIsAllowed(true);
      setIsChecking(false);
      setHasVerified(true);
      return;
    }

    setIsChecking(true);
    setError(null);

    const cached = getCachedLocation();
    // Only use cache when the result allows access. A cached "blocked" result
    // would silently deny the user on every revisit without ever asking for
    // browser location — so we always re-check when the cached verdict is denied.
    if (cached && cached.isAllowed) {
      setLocationData(cached);
      setIsAllowed(true);
      setIsChecking(false);
      setHasVerified(true);
      setPermissionState(cached.permissionState || null);
      setIsLocationDeniedOrIgnored(!!cached.isLocationDeniedOrIgnored);
      return;
    }

    try {
      const organizationId = config.tlfId;
      // Strip port number from IP if present (e.g. "1.2.3.4:27501" → "1.2.3.4")
      const ip = (config.clientIp || '').split(':')[0];

      // Strategy: Browser geo FIRST — gives the user a fair chance to share
      // their precise location before we fall back to an IP-based verdict.
      // - 'granted':  get position instantly (no prompt) → API with coords
      // - 'prompt':   show the prompt, give user 10s to respond → fall back to IP
      // - 'unknown':  Permissions API unavailable; still attempt geolocation (8s)
      // - 'denied':   skip browser geo entirely, fall back to IP-only result
      let latitude = 0, longitude = 0;
      let finalPermState = 'unknown';
      const permState = await getGeoPermissionState();

      if (permState === 'granted') {
        // Permission already granted — resolves instantly, no prompt
        const pos = await getBrowserPosition(2000);
        latitude = pos.latitude;
        longitude = pos.longitude;
        finalPermState = pos.status || 'granted';
      } else if (permState === 'prompt') {
        // Show the prompt — give the user enough time to notice and respond
        const pos = await getBrowserPosition(10000);
        latitude = pos.latitude;
        longitude = pos.longitude;
        finalPermState = pos.status || (latitude !== 0 ? 'granted' : 'timed_out');
      } else if (permState === 'unknown') {
        // Permissions API unavailable — still attempt geolocation; it may succeed
        // (e.g. older browsers that support geolocation but not navigator.permissions)
        const pos = await getBrowserPosition(8000);
        latitude = pos.latitude;
        longitude = pos.longitude;
        finalPermState = pos.status || (latitude !== 0 ? 'granted' : 'unknown');
      } else if (permState === 'denied') {
        finalPermState = 'denied';
      }

      let data;
      if (latitude !== 0 && longitude !== 0) {
        // Browser coords available — make a fresh call with coords (saves IP lookup cost)
        const response = await api.get(apiRoutes.LocationAccess, {
          params: { organizationId, ip, latitude, longitude },
        });
        data = response.data;
      } else {
        // No browser coords — use the prefetched IP-only result (fired at module load)
        try {
          const prefetched = prefetchedLocationAccess ? await prefetchedLocationAccess : null;
          if (prefetched) {
            data = prefetched.data;
          } else {
            const response = await api.get(apiRoutes.LocationAccess, { params: { organizationId, ip } });
            data = response.data;
          }
        } catch {
          // Prefetch rejected — try fresh
          const response = await api.get(apiRoutes.LocationAccess, { params: { organizationId, ip } });
          data = response.data;
        }
      }

      const deniedOrIgnored = finalPermState === 'denied' || finalPermState === 'timed_out';
      const result = {
        ip: data.ip || ip || '',
        city: data.city || '',
        region: data.region || '',
        regionCode: data.regionCode || '',
        country: data.country || '',
        countryCode: data.countryCode || '',
        isAllowed: data.siteAccessAllowed ?? true,
        allowedRegions: normalizeAllowedRegions(data.allowedRegions),
        permissionState: finalPermState,
        isLocationDeniedOrIgnored: deniedOrIgnored,
        checkedAt: new Date().toISOString(),
      };

      setLocationData(result);
      setIsAllowed(result.isAllowed);
      setPermissionState(finalPermState);
      setIsLocationDeniedOrIgnored(deniedOrIgnored);
      setHasVerified(true);
      // Only cache allowed results. Never cache a blocked verdict — the user
      // should get a fresh check (and a browser-location prompt) on every visit
      // until they're confirmed to be in the right region.
      if (result.isAllowed) {
        setCachedLocation(result);
      }
    } catch {
      // Permissive fallback: if backend is unreachable, don't block users
      setIsAllowed(true);
      setHasVerified(true);
      setError('Unable to verify location');
    } finally {
      setIsChecking(false);
    }
  }, []);

  useEffect(() => {
    if (config.requireLocation && !isBypassed) {
      if (geoBlockStage === 'SiteLoad' || cachedInit) {
        checkLocation();
      }
    }
  }, [checkLocation, config.requireLocation, isBypassed, geoBlockStage]);

  const clearCache = useCallback(() => {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem('geo_bypass_acknowledged');
    setLocationData(null);
    setIsAllowed(true);
    setIsChecking(true);
    setHasVerified(false);
    setPermissionState(null);
    setIsLocationDeniedOrIgnored(false);
    checkLocation();
  }, [checkLocation]);

  const acknowledgeBypass = useCallback(() => {
    localStorage.setItem('geo_bypass_acknowledged', 'true');
    setIsAllowed(true);
    setLocationData((prev) => (prev ? { ...prev, isAllowed: true } : { isAllowed: true }));
  }, []);

  return (
    <GeoLocationContext.Provider value={{
      locationData,
      isAllowed,
      isChecking,
      hasVerified,
      permissionState,
      isLocationDeniedOrIgnored,
      geoBlockStage,
      verifyLocation: checkLocation,
      error,
      // Prefer the org's real allowed regions from the backend; fall back only when
      // the API didn't supply them (older build / pre-upgrade cached result).
      allowedRegions: (locationData?.allowedRegions && locationData.allowedRegions.length > 0)
        ? locationData.allowedRegions
        : FALLBACK_ALLOWED_REGIONS,
      recheckLocation: clearCache,
      acknowledgeBypass,
    }}>
      {children}
    </GeoLocationContext.Provider>
  );
}

export function useGeoLocation() {
  const context = useContext(GeoLocationContext);
  if (context === undefined) {
    throw new Error('useGeoLocation must be used within a GeoLocationProvider');
  }
  return context;
}
